// @ts-check
import { collection, getDocs, query, where } from "firebase/firestore";
import { tolerantId } from "./firestoreHelpers";
import { esPermisoDenegado, leerMiAcceso } from "./consultasEdificio";

/**
 * Lecturas del ex-ocupante en las pantallas de residente (Fase 0D, C12).
 *
 * multas y expenseclaims se leen con leeEdificioOPropio(buildingid, titular…)
 * de firestore.0d.rules: el staff y los residentes vigentes (la demo en 99999)
 * ven todo el edificio; el ex-ocupante (formerBids) solo lo suyo, y una
 * consulta que no fija el titular se le niega ENTERA (las reglas no filtran).
 * Para él la consulta lleva where('buildingid','in',…) + where(<titular>,'in',
 * sus userids), una por cada campo de titular (userid; en multas también
 * responsibleUserId: el campo que la consulta no fija da error en la regla y
 * el || lo descarta).
 */

// Variantes de userid por consulta: × 2 variantes del edificio ([10, "10"]) =
// 28 disyunciones, debajo del tope de 30 de Firestore.
const VARIANTES_POR_CONSULTA = 14;

/**
 * Variantes number/string de cada userid, sin repetir, para un `in`.
 *
 * @param {Array<string | number | null | undefined>} ids - Userids
 * @returns {Array<string | number>}
 */
export function variantesDeIds(ids) {
  const porClave = new Map();
  (ids || [])
    .filter((id) => id != null && String(id).trim() !== "")
    .flatMap((id) => tolerantId(typeof id === "string" ? id.trim() : id))
    .forEach((v) => porClave.set(`${typeof v}:${v}`, v));
  return [...porClave.values()];
}

/**
 * userid canónico para comparar en el cliente, igual que idKey de las reglas:
 * 102, "102" y "0102" dan "102"; los no numéricos quedan igual.
 *
 * @param {unknown} v - userid
 */
export const claveDeId = (v) => {
  const s = String(v ?? "").trim();
  return /^\d+$/.test(s) ? String(Number(s)) : s;
};

/**
 * Userids con los que el ex-ocupante fija sus consultas: los de su cuenta según
 * el servidor (user_access.userids), que es lo que mira la regla; un userid que
 * no esté ahí haría negar la consulta entera. Sin sesión ("ver como": quien
 * mira es staff del edificio y la regla le deja leer todo) o sin user_access,
 * los de respaldo (el userid de la ficha que muestra la pantalla).
 *
 * @param {import("firebase/firestore").Firestore} db - Instancia de Firestore
 * @param {{ uid?: string | null, respaldo?: Array<string | number | null | undefined> }} [opciones]
 * @returns {Promise<Array<string | number>>}
 */
export async function useridsDeLaCuenta(db, { uid = null, respaldo = [] } = {}) {
  const validos = (lista) => (lista || []).filter((x) => x != null && String(x).trim() !== "");
  if (uid) {
    const acceso = await leerMiAcceso(db, uid);
    const propios = validos(Array.isArray(acceso?.userids) ? acceso.userids : []);
    if (propios.length > 0) return propios;
  }
  return validos(respaldo);
}

/**
 * Docs de un edificio con datos de una persona (multas, expenseclaims) para una
 * pantalla de residente.
 *  - Sin soloPropias: primero la consulta del edificio (residente vigente, demo,
 *    staff en "ver como"; la pantalla filtra lo suyo). Si la regla la niega
 *    (ex-ocupante cuyo edificio todavía no figura como de solo lectura), se
 *    repite con la forma C12.
 *  - Con soloPropias (edificio de solo lectura: ya se sabe que es ex-ocupante):
 *    directo la forma C12, sin la consulta que se negaría.
 * Forma C12: una consulta por campo de titular y por tanda de userids. Si un
 * campo se niega y otro pasa, quedan los docs del que pasó (la consulta por
 * responsibleUserId depende de que la regla descarte el userid sin fijar); si
 * se niegan todos, se propaga el error. Cualquier otro error (red, índice)
 * se propaga.
 *
 * @param {import("firebase/firestore").Firestore} db - Instancia de Firestore
 * @param {string} coleccion - multas | expenseclaims
 * @param {string | number} buildingId - Edificio
 * @param {{
 *   soloPropias?: boolean,
 *   campos?: string[],
 *   propios?: Array<string | number | null | undefined> | (() => Promise<Array<string | number | null | undefined>>),
 *   extras?: Array<any>,
 * }} [opciones]
 *   - campos: campos de titular (["userid"] por defecto)
 *   - propios: userids propios, o una función que los resuelve (solo se llama
 *     si hace falta la forma C12)
 *   - extras: filtros adicionales para todas las consultas (p. ej. el estado)
 * @returns {Promise<Array<Record<string, any> & { id: string }>>}
 */
export async function leerPropiasDelEdificio(
  db,
  coleccion,
  buildingId,
  { soloPropias = false, campos = ["userid"], propios = [], extras = [] } = {}
) {
  const col = collection(db, coleccion);
  const delEdificio = where("buildingid", "in", tolerantId(buildingId));
  let negadaPorEdificio = null;
  if (!soloPropias) {
    try {
      const snap = await getDocs(query(col, delEdificio, ...extras));
      return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    } catch (e) {
      if (!esPermisoDenegado(e)) throw e;
      negadaPorEdificio = e;
    }
  }

  const ids = typeof propios === "function" ? await propios() : propios;
  const variantes = variantesDeIds(ids);
  if (variantes.length === 0) {
    if (negadaPorEdificio) throw negadaPorEdificio;
    return [];
  }

  const porId = new Map();
  let algunCampoPaso = false;
  let primerNegado = null;
  for (const campo of campos) {
    try {
      for (let i = 0; i < variantes.length; i += VARIANTES_POR_CONSULTA) {
        const tanda = variantes.slice(i, i + VARIANTES_POR_CONSULTA);
        const snap = await getDocs(query(col, delEdificio, where(campo, "in", tanda), ...extras));
        snap.docs.forEach((d) => porId.set(d.id, { id: d.id, ...d.data() }));
      }
      algunCampoPaso = true;
    } catch (e) {
      if (!esPermisoDenegado(e)) throw e;
      if (!primerNegado) primerNegado = e;
      console.warn(`[C12] ${coleccion} por ${campo}: la regla negó la consulta`);
    }
  }
  if (!algunCampoPaso && primerNegado) throw primerNegado;
  return [...porId.values()];
}
